// src/contexts/NotificationContext.js

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification debe usarse dentro de NotificationProvider');
    }
    return context;
};

function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export const NotificationProvider = ({ children }) => {
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info' // 'success', 'error', 'warning', 'info'
    });

    const showNotification = useCallback((message, severity = 'info') => {
        setNotification({
            open: true,
            message,
            severity
        });
    }, []);

    const showSuccess = useCallback((message) => showNotification(message, 'success'), [showNotification]);
    const showError = useCallback((message) => showNotification(message, 'error'), [showNotification]);
    const showWarning = useCallback((message) => showNotification(message, 'warning'), [showNotification]);
    const showInfo = useCallback((message) => showNotification(message, 'info'), [showNotification]);

    const handleClose = useCallback((event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setNotification(prev => ({ ...prev, open: false }));
    }, []);

    const contextValue = useMemo(() => ({
        showNotification, showSuccess, showError, showWarning, showInfo
    }), [showNotification, showSuccess, showError, showWarning, showInfo]);

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} 
            >
                <Alert onClose={handleClose} severity={notification.severity}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
};